import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { chatgenieAPI } from 'src/config/api';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import queryKeys from 'src/constants/queryKeys';
import useToast from 'src/hooks/useToast';
import SimpleButton from 'src/components/SimpleButton';
import { Checkbox } from '@Thread-Magic/jasmine';
import BotStyleBlock from 'src/components/BotStyleBlock';
import theme from 'src/config/theme';
import * as Styled from 'src/components/MessengerSettings/style';
import { startCase } from 'lodash';
import { transformToCamelCase } from 'src/utils/data-transformer';
import MessengerStyleBlock from './StyleBlock';
import MessengerWidget from '../MessengerSettings/MessengerWidget';
import SimpleTextarea from '../SimpleTextarea';
import Switcher from '../Switcher';

const DEFAULT_MESSENGER_DESKTOP_ICON =
  'https://chatgenie-messenger.s3.amazonaws.com/downloads/desktop/Messenger+Desktop+Logo.png';

const MessengerDesign = ({ companyId, appId, parentCompanyId }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isParentCompany = () => parentCompanyId === companyId;
  const [overrideDesign, setOverrideDesign] = useState(0);
  const [messengerStyle, setMessengerStyle] = useState({
    primaryColor: theme.messenger.default.primaryColors[0],
    logo: {
      id: null,
      url: `${window.location.origin}${theme.messenger.default.logo}`
    },
    trayIcon: {
      id: null,
      url: `${window.location.origin}${theme.messenger.default.trayIcon}`
    },
    desktopIcon: {
      id: null,
      url: DEFAULT_MESSENGER_DESKTOP_ICON
    }
  });
  const [botStyle, setBotStyle] = useState({
    botName: theme.messenger.default.botName,
    botIcon: {
      id: null,
      url: `${window.location.origin}${theme.messenger.default.botLogos[0]}`
    }
  });

  const { data: designSettings, isLoading: isLoadingDesignSettings } = useQuery(
    [queryKeys.GET_MESSENGER_DESIGN],
    () => chatgenieAPI.getMessengerDesign()
  );

  const { data: clientDesignSettings, isLoading: isLoadingClientDesignSettings } = useQuery(
    [queryKeys.GET_MESSENGER_DESIGN, companyId],
    () => chatgenieAPI.getCompanyMessengerDesign({ companyId }),
    { enabled: !isParentCompany() }
  );

  const [messengerSettingsData, setMessengerSettingsData] = useState();

  const { data: messengerParentSettings, isLoading: isMessengerSettingsLoading } = useQuery(
    [queryKeys.GET_MESSENGER_SETTINGS, parentCompanyId],
    () => chatgenieAPI.getMessengerSettings()
  );
  const { data: messengerCompanySettings } = useQuery(
    [queryKeys.GET_MESSENGER_SETTINGS, companyId],
    () => chatgenieAPI.getCompanyMessengerSettings(companyId)
  );

  useEffect(() => {
    if (messengerParentSettings && messengerCompanySettings) {
      const settingsData = isParentCompany()
        ? messengerParentSettings
        : messengerCompanySettings?.id
        ? messengerCompanySettings
        : messengerParentSettings;

      setMessengerSettingsData({
        ...transformToCamelCase(settingsData)
      });
    }
  }, [messengerParentSettings, messengerCompanySettings]);

  const mutateMessengerSettings = useMutation(
    data => {
      if (isParentCompany()) {
        return chatgenieAPI.updateMessengerSettings(data);
      }
      if (messengerCompanySettings?.id) {
        return chatgenieAPI.updateCompanyMessengerSettings({ data, companyId });
      }
      return chatgenieAPI.createCompanyMessengerSettings({ data, companyId });
    },
    {
      onSuccess: res => {
        queryClient.setQueryData(
          [queryKeys.GET_MESSENGER_SETTINGS, isParentCompany() ? parentCompanyId : companyId],
          res
        );
        toast.success('Successfully saved');
      },
      onError: error => {
        let errorMessage = error.response?.data?.message || error.message;

        if (error.response?.data?.errors) {
          errorMessage = Object.values(error.response?.data?.errors).reduce(
            (acc, error) => `${acc} ${error.join(' ')}`,
            `${errorMessage} `
          );
        }

        toast.error(errorMessage);
      }
    }
  );

  const isLoadingAllSettings =
    isLoadingDesignSettings ||
    isLoadingClientDesignSettings ||
    isMessengerSettingsLoading ||
    !messengerSettingsData;

  const mutateDesignSettings = useMutation(data => chatgenieAPI.updateMessengerDesign({ data }), {
    onSuccess: res => {
      queryClient.setQueryData([queryKeys.GET_MESSENGER_DESIGN], res);
      toast.success('Successfully saved');
    },
    onError: error => {
      toast.error(error.message);
    }
  });

  const mutateCompanyDesignSettings = useMutation(
    data =>
      clientDesignSettings.id
        ? chatgenieAPI.updateCompanyMessengerDesign({ data, companyId })
        : chatgenieAPI.createCompanyMessengerDesign({ data, companyId }),
    {
      onSuccess: res => {
        queryClient.setQueryData([queryKeys.GET_MESSENGER_DESIGN, companyId], res);
        toast.success('Successfully saved');
      },
      onError: error => {
        toast.error(error.message);
      }
    }
  );

  const updateSettings = async settings => {
    setMessengerStyle({
      primaryColor: settings?.primary_color || theme.messenger.default.primaryColors[0],
      logo: {
        id: settings?.logo?.id || null,
        url: settings?.logo?.path || `${window.location.origin}${theme.messenger.default.logo}`
      },
      trayIcon: {
        id: settings?.tray_icon?.id || null,
        url:
          settings?.tray_icon?.path ||
          `${window.location.origin}${theme.messenger.default.trayIcon}`
      },
      desktopIcon: {
        id: settings?.desktop_icon?.id || null,
        url: settings?.desktop_icon?.path || DEFAULT_MESSENGER_DESKTOP_ICON
      }
    });

    setBotStyle({
      botName: settings?.bot_name || theme.messenger.default.botName,
      botIcon: {
        id: settings?.bot_icon?.id || null,
        url:
          settings?.bot_icon?.path ||
          `${window.location.origin}${theme.messenger.default.botLogos[0]}`
      }
    });
  };

  useEffect(() => {
    if (designSettings || clientDesignSettings) {
      const settings = clientDesignSettings?.id ? clientDesignSettings : designSettings;
      if (!isParentCompany())
        setOverrideDesign(!clientDesignSettings?.id ? 0 : settings.override_design);
      updateSettings(settings, !clientDesignSettings?.id);
    }
  }, [designSettings, clientDesignSettings]);

  const onFormSubmit = event => {
    event.preventDefault();

    const currentDesign = {
      logo_id: messengerStyle.logo?.id,
      tray_icon_id: messengerStyle.trayIcon?.id,
      bot_icon_id: botStyle.botIcon?.id,
      bot_name: botStyle.botName,
      desktop_icon_id: messengerStyle.desktopIcon?.id,
      primary_color: messengerStyle.primaryColor
    };

    const parentDesign = {
      logo_id: designSettings.logo?.id,
      tray_icon_id: designSettings.tray_icon?.id,
      bot_icon_id: designSettings.bot_icon?.id,
      desktop_icon_id: designSettings.desktop_icon?.id,
      bot_name: designSettings.bot_name || theme.messenger.default.botName,
      primary_color: designSettings.primary_color || theme.messenger.default.primaryColors[0]
    };

    const currentSettings = {
      override_bot_prompt_messages: messengerSettingsData.overrideBotPromptMessages,
      ask_for_configuration: messengerSettingsData.askForConfiguration,
      bot_prompt_message_translations:
        messengerSettingsData.botPromptMessageTranslations[messengerSettingsData.lang]
    };

    if (isParentCompany()) {
      mutateDesignSettings.mutate(currentDesign);
    } else {
      mutateCompanyDesignSettings.mutate({
        ...(overrideDesign ? { ...currentDesign } : { ...parentDesign }),
        override_design: overrideDesign
      });
    }
    mutateMessengerSettings.mutate(currentSettings);
  };

  return (
    <Styled.LocalPageHolder isLoading={isLoadingAllSettings}>
      <Styled.Content>
        {!isLoadingAllSettings && (
          <Styled.Body>
            <Styled.Form
              id="save-settings"
              onSubmit={onFormSubmit}
              disabled={!isParentCompany() && !overrideDesign}
            >
              {!isParentCompany() && (
                <>
                  <Styled.FormGroup>
                    <Checkbox
                      className="override-design"
                      id="override-design"
                      checked={Boolean(overrideDesign)}
                      onChange={() => setOverrideDesign(overrideDesign ? 0 : 1)}
                    >
                      Override parent design
                    </Checkbox>
                  </Styled.FormGroup>
                  <hr />
                </>
              )}
              <Styled.Title marginTop={10}>Messenger Design</Styled.Title>
              <Styled.Description sub>
                Customize the styling, branding, and behavior of your chat experience.
              </Styled.Description>
              <MessengerStyleBlock state={messengerStyle} onChangeState={setMessengerStyle} />
              <BotStyleBlock state={botStyle} onChangeState={setBotStyle} />
              <Styled.Title>Chatbot messages</Styled.Title>

              <Styled.FormGroup oneLine>
                <div>
                  <Styled.SubTitle>Request configuration details</Styled.SubTitle>
                  <Styled.Description>
                    Automatically ask contacts to specify the device or configuration related to
                    their request when a thread is created.
                  </Styled.Description>
                </div>
                <Switcher
                  className="proactive-messages"
                  id="ask-for-configuration"
                  checked={Boolean(messengerSettingsData.askForConfiguration)}
                  onChange={() =>
                    setMessengerSettingsData(old => ({
                      ...old,
                      askForConfiguration: old.askForConfiguration ? 0 : 1
                    }))
                  }
                  controlText={{ checked: 'ON', unchecked: 'OFF' }}
                />
              </Styled.FormGroup>

              <Styled.FormGroup oneLine>
                <div>
                  <Styled.SubTitle>Custom bot replies</Styled.SubTitle>
                  <Styled.Description>
                    Edit the greetings and replies used by your automated chatbot to match your
                    branding and tone of voice.
                  </Styled.Description>
                </div>
                <Switcher
                  className="proactive-messages"
                  id="is-active-flow"
                  checked={Boolean(messengerSettingsData.overrideBotPromptMessages)}
                  onChange={() =>
                    setMessengerSettingsData(old => ({
                      ...old,
                      overrideBotPromptMessages: old.overrideBotPromptMessages ? 0 : 1
                    }))
                  }
                  controlText={{ checked: 'ON', unchecked: 'OFF' }}
                />
              </Styled.FormGroup>
              {!!messengerSettingsData.overrideBotPromptMessages &&
                Object.keys(
                  messengerSettingsData.botPromptMessageTranslations[messengerSettingsData.lang]
                ).map(key => (
                  <Styled.FormGroup key={key}>
                    <Styled.SubTitle>{startCase(key)}</Styled.SubTitle>
                    <SimpleTextarea
                      id={key}
                      autoresize
                      type="text"
                      name={key}
                      value={
                        messengerSettingsData.botPromptMessageTranslations[
                          messengerSettingsData.lang
                        ][key]
                      }
                      placeholder={
                        messengerSettingsData.botPromptMessageTranslations[
                          messengerSettingsData.lang
                        ][key]
                      }
                      onChange={event => {
                        const { value } = event.target;
                        setMessengerSettingsData(old => ({
                          ...old,
                          botPromptMessageTranslations: {
                            ...old.botPromptMessageTranslations,
                            [messengerSettingsData.lang]: {
                              ...messengerSettingsData.botPromptMessageTranslations[
                                messengerSettingsData.lang
                              ],
                              [key]: value
                            }
                          }
                        }));
                      }}
                    />
                    {!messengerSettingsData.botPromptMessageTranslations[
                      messengerSettingsData.lang
                    ][key] && <Styled.InputError>This is a required field</Styled.InputError>}
                  </Styled.FormGroup>
                ))}
              <SimpleButton
                isLoader={isLoadingAllSettings}
                type="submit"
                formId="save-settings"
                size="small"
              >
                Save
              </SimpleButton>
            </Styled.Form>
          </Styled.Body>
        )}
        {!isLoadingAllSettings && (
          <Styled.Aside>
            <MessengerWidget
              appId={appId}
              data={{
                ...messengerStyle,
                ...botStyle
              }}
              shouldUseSavedData={false}
            />
          </Styled.Aside>
        )}
      </Styled.Content>
    </Styled.LocalPageHolder>
  );
};

MessengerDesign.propTypes = {
  companyId: PropTypes.number.isRequired,
  parentCompanyId: PropTypes.number.isRequired,
  appId: PropTypes.string.isRequired
};

const mapStateToProps = state => ({
  parentCompanyId: state.usersReducer.user.company.id
});

export default connect(mapStateToProps)(MessengerDesign);
